import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useResidenceData } from '../Demographic/useResidenceData'

import { getAge } from './functionsValidation'

import {
    AutocompleteControl,
    DatePickerControl
} from '@/components/form-controls'
import { BasicInfoFormData } from '@/types/forms/basicInfoForm'
import { DateParser } from '@/utils/dateParser'

interface DocumentExpeditionControlProps {
    control: Control<BasicInfoFormData>
    watchCountryExpedition: string
    watchDepartmentExpedition: string
    handleFieldChange: (field: string, value: string) => void
    watchBirthDate: string
    watchDocumentType: string
    text: string
    trigger?: (name: keyof BasicInfoFormData) => Promise<boolean>
    isFormTouched?: boolean
}

export const DocumentExpeditionControl = ({
    control,
    watchCountryExpedition,
    watchDepartmentExpedition,
    handleFieldChange,
    watchBirthDate,
    watchDocumentType,
    text,
    trigger,
    isFormTouched = false
}: DocumentExpeditionControlProps): JSX.Element => {
    const { t } = useTranslation()

    const { countries, idCityResidence, residenceDepartments } =
        useResidenceData(watchCountryExpedition, watchDepartmentExpedition)

    return (
        <>
            <DatePickerControl
                name="documentExpeditionDate"
                control={control}
                label={t(`${text}.documentExpeditionDate`)}
                maxDate={new Date(Date.now())}
                onCustomChange={() => {
                    // Revalida inmediatamente cuando el usuario cambia la fecha de expedición
                    if (trigger) {
                        void trigger('documentExpeditionDate')
                    }
                }}
                rules={{
                    validate: (value: Date | string) => {
                        // No validar nada si el campo está vacío y el formulario no ha sido tocado
                        if (!value && !isFormTouched) {
                            return true
                        }

                        // Validar requerido
                        if (!value) {
                            return t(`${text}.required.documentExpeditionDate`)
                        }

                        const birthDateValue = watchBirthDate

                        if (!birthDateValue) {
                            return true
                        }

                        const expeditionDate = DateParser.parseDate(
                            String(value)
                        )
                        const birth = DateParser.parseDate(
                            String(birthDateValue)
                        )

                        if (!expeditionDate || !birth) {
                            return true
                        }

                        // Validar que la fecha de expedición no sea anterior a la fecha de nacimiento
                        if (expeditionDate < birth) {
                            return t(
                                `${text}.validation.documentExpeditionAfterBirth`
                            )
                        }

                        // Si es cédula de ciudadanía, validar que la expedición sea al menos 18 años después del nacimiento
                        if (
                            (watchDocumentType === 'cc' ||
                                watchDocumentType === 'FS0010') &&
                            getAge(birth, expeditionDate) < 18
                        ) {
                            return t(
                                `${text}.required.min18YearsExpeditionDate`
                            )
                        }

                        return true
                    }
                }}
            />
            <AutocompleteControl
                name="countryExpedition"
                control={control}
                label={t(`${text}.countryExpedition`)}
                options={countries}
                rules={{
                    required: t(`${text}.required.countryExpedition`)
                }}
                onChangeCustom={(value) => {
                    // Limpiar departamento y ciudad cuando cambie el país
                    handleFieldChange?.('countryExpedition', value)
                }}
            />
            <AutocompleteControl
                name="departmentExpedition"
                control={control}
                label={t(`${text}.departmentExpedition`)}
                options={residenceDepartments}
                placeholder={
                    !watchCountryExpedition
                        ? t('forms.common.placeholders.selectCountryFirst')
                        : t(`${text}.departmentExpedition`)
                }
                isDisabled={!watchCountryExpedition}
                rules={{
                    required: t(`${text}.required.departmentExpedition`)
                }}
                onChangeCustom={(value) => {
                    // Limpiar la ciudad cuando cambie el departamento
                    handleFieldChange?.('departmentExpedition', value)
                }}
            />
            {watchCountryExpedition === 'CO' && (
                <AutocompleteControl
                    name="expeditionDocumentPlace"
                    control={control}
                    label={t(`${text}.cityExpedition`)}
                    options={idCityResidence.map((item) => ({
                        value: item.label,
                        label: item.label
                    }))}
                    placeholder={
                        !watchDepartmentExpedition
                            ? t('forms.common.placeholders.selectDepartmentFirst')
                            : t(`${text}.cityExpedition`)
                    }
                    isDisabled={!watchDepartmentExpedition}
                    rules={{
                        required: t(`${text}.required.cityExpedition`)
                    }}
                    onChangeCustom={(value) => {
                        handleFieldChange?.('expeditionDocumentPlace', value)
                    }}
                />
            )}
        </>
    )
}
